import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import API from "../lib/api";
import { CheckCircle, XCircle, Gem, Loader2 } from "lucide-react";
import { useSiteSettings } from "../contexts/SiteSettingsContext";

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { siteTitle } = useSiteSettings();
  const [status, setStatus] = useState("verifying");
  const [message, setMessage] = useState("");
  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("Missing verification token.");
      return;
    }
    API.post("/auth/verify-email", { token })
      .then(() => {
        setStatus("success");
        setMessage("Your email has been verified!");
        setTimeout(() => navigate("/login"), 3000);
      })
      .catch((err) => {
        setStatus("error");
        setMessage(err.response?.data?.detail || "Verification failed. The link may be invalid or expired.");
      });
  }, [token, navigate]);

  return (
    <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center px-4" data-testid="verify-email-page">
      <div className="w-full max-w-md text-center">
        <Link to="/" className="inline-flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-[#00F0FF]/10 flex items-center justify-center">
            <Gem className="w-6 h-6 text-[#00F0FF]" />
          </div>
          <span className="text-2xl font-bold text-[#F8FAFC]">{siteTitle}</span>
        </Link>

        {status === "verifying" && (
          <div className="bg-[#131722] rounded-xl border border-[#2A3241] p-8" data-testid="verify-loading">
            <Loader2 className="w-12 h-12 text-[#00F0FF] mx-auto mb-4 animate-spin" />
            <h2 className="text-xl font-bold text-[#F8FAFC] mb-2">Verifying your email...</h2>
            <p className="text-sm text-[#94A3B8]">Please wait</p>
          </div>
        )}

        {status === "success" && (
          <div className="bg-[#131722] rounded-xl border border-[#10B981]/30 p-8" data-testid="verify-success">
            <div className="w-16 h-16 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-[#10B981]" />
            </div>
            <h2 className="text-xl font-bold text-[#F8FAFC] mb-2">{message}</h2>
            <p className="text-sm text-[#94A3B8] mb-6">Redirecting to login...</p>
            <Link to="/login" className="inline-block px-6 py-3 text-sm font-bold uppercase tracking-widest bg-[#00F0FF] text-[#0B0E14] rounded-lg hover:bg-[#00F0FF]/90" data-testid="verify-login-btn">
              Go to Login
            </Link>
          </div>
        )}

        {status === "error" && (
          <div className="bg-[#131722] rounded-xl border border-[#EF4444]/30 p-8" data-testid="verify-error">
            <div className="w-16 h-16 rounded-full bg-[#EF4444]/10 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-[#EF4444]" />
            </div>
            <h2 className="text-xl font-bold text-[#F8FAFC] mb-2">Verification Failed</h2>
            <p className="text-sm text-[#94A3B8] mb-6">{message}</p>
            <Link to="/register" className="inline-block px-6 py-3 text-sm font-bold uppercase tracking-widest bg-[#00F0FF] text-[#0B0E14] rounded-lg hover:bg-[#00F0FF]/90" data-testid="verify-register-btn">
              Back to Register
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
